/**
 * Projects API Service
 *
 * Handles all API calls for Strategy Projects.
 * Projects are containers for strategy code and related experiments.
 */

import { apiClient } from './client';
import type {
  ProjectIntelligence,
  ProjectIntelligenceSummary,
  ProjectIntelligenceDimensions,
} from '../../types/projectIntelligence';

// ============================================================================
// Types
// ============================================================================

export interface ProjectConfig {
  symbol: string;
  interval: string;
  portfolioAmount: number;
  portfolioCurrency: string;
  tradingMode: string;
}

export interface ValidationAsset {
  symbol: string;
  interval: string;
}

export interface ProjectStats {
  backtestCount: number;
  optimizationCount: number;
  wfaCount: number;
  versionCount: number;
  baselineScanCount: number;
  baselineOptimizationCount: number;
  bestReturn?: number;
  bestReturnPercent?: number;
  lastRunAt?: string;
  validationAssets?: ValidationAsset[];
}

export interface CodeVersion {
  _id: string;
  hash: string;
  tag?: string;
  message?: string;
  createdAt: string;
}

export interface CloneSource {
  type: 'template' | 'project';
  sourceId: string;
  sourceName: string;
}

/** Phase where user last did meaningful work */
export type ProjectPhase = 'definition' | 'backtest' | 'results';

export interface StrategyProject {
  _id: string;
  name: string;
  description?: string;
  tags: string[];
  notes?: string;
  userId: string;
  code: string;
  codeVersions: CodeVersion[];
  currentVersionId?: string;
  defaultConfig: ProjectConfig;
  parameterSchema: Record<string, unknown>;
  parameters: Record<string, unknown>;
  clonedFrom?: CloneSource;
  supportedTradingModes?: string[];
  stats: ProjectStats;
  lastActivePhase?: ProjectPhase;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string;
}

export interface StrategyTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  difficulty: string;
  defaultConfig: ProjectConfig;
  parameterSchema: Record<string, unknown>;
  defaultParams: Record<string, unknown>;
  code?: string; // Only included when fetching single template
}

export interface ProjectExperiment {
  _id: string;
  name: string;
  symbol: string;
  interval: string;
  strategy: string;
  params: Record<string, unknown>;
  stats: {
    totalReturnPercent?: number;
    totalTrades?: number;
    winRate?: number;
    sharpeRatio?: number;
    maxDrawdownPercent?: number;
    profitFactor?: number;
    // Enhanced stats
    enhanced?: Record<string, unknown>;
    drawdown?: Record<string, unknown>;
    regimes?: Record<string, unknown>;
  };
  codeHash?: string;
  versionId?: string;
  createdAt: string;
  source?: string;
  tradeCount?: number;
}

/**
 * Run Types for unified Results page
 */
export type RunType = 'backtest' | 'optimization' | 'wfa' | 'robustness' | 'baseline';

/**
 * Unified Run - represents any execution result (backtest, optimization, etc.)
 */
export interface ProjectRun {
  _id: string;
  runType: RunType;
  jobId?: string; // For optimization jobs
  name: string;
  symbol: string;
  interval: string;
  strategy: string;
  params: Record<string, unknown>;
  stats: {
    totalReturnPercent?: number;
    sharpeRatio?: number;
    maxDrawdownPercent?: number;
    profitFactor?: number;
    winRate?: number;
    tradeCount?: number;
    // Optimization-specific
    iterationsCompleted?: number;
    resultsCount?: number;
    // Baseline-specific
    consistencyScore?: number;
    robustnessScore?: number;
    assetsCount?: number;
    outcome?: string;
  };
  bestParams?: Record<string, unknown>; // For optimization runs
  baselineData?: {
    outcomeClassification: string;
    stableRegion?: Record<string, unknown>;
    aggregates?: Record<string, unknown>;
    assets?: string[];
    rankedResults?: Array<Record<string, unknown>>;
    config?: Record<string, unknown>;
    duration?: number;
  };
  versionId?: string;
  createdAt: string;
  source?: string;
  dateRange?: {
    start: string;
    end: string;
  } | null;
  tweakCount?: number;
}

/**
 * Embedded trade data for full backtest retrieval
 */
export interface EmbeddedTrade {
  openedAt: string;
  closedAt: string;
  tradeType: 'long' | 'short';
  symbol: string;
  avgEntry: number;
  avgExit: number;
  quantity: number;
  totalProfit: number;
  profitPercent: number;
  totalFees?: number;
  status: 'open' | 'closed' | 'cancelled';
  duration?: number;
  portfolioBeforeTrade?: number;
  portfolioAfterTrade?: number;
  entries?: Array<{
    price: number;
    quantity: number;
    timestamp: string;
    fee?: number;
  }>;
  exits?: Array<{
    price: number;
    quantity: number;
    timestamp: string;
    fee?: number;
  }>;
}

/**
 * Full backtest with embedded trades for Results page
 */
export interface FullBacktest extends ProjectExperiment {
  embeddedTrades: EmbeddedTrade[];
  signals?: Array<{
    timestamp: string;
    open: number;
    high: number;
    low: number;
    close: number;
    signal?: number;
    [key: string]: unknown;
  }>;
  codeSnapshot?: string;
  portfolioAmount: number;
  portfolioCurrency: string;
  /** User notes for this specific run (run-scoped analysis notes) */
  runNotes?: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  tags?: string[];
  defaultConfig?: Partial<ProjectConfig>;
  ownershipConfirmed?: boolean;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  tags?: string[];
  code?: string;
  defaultConfig?: Partial<ProjectConfig>;
  parameters?: Record<string, unknown>;
  parameterSchema?: Record<string, unknown>;
  notes?: string;
  lastActivePhase?: ProjectPhase;
  currentVersionId?: string;
}

export interface SaveVersionRequest {
  tag?: string;
  message?: string;
}

export interface SaveExperimentRequest {
  backtest: {
    name: string;
    symbol: string;
    interval: string;
    strategy: string;
    portfolioAmount: number;
    portfolioCurrency: string;
    params: Record<string, unknown>;
    stats: Record<string, unknown>;
    trades: unknown[];
    signals?: unknown[];
    source?: 'manual' | 'research-lab' | 'optimization' | 'wfa' | 'quick-tweak';
    parentBacktestId?: string;
  };
  code: string;
}

// Multi-file project types
export interface ProjectFile {
  path: string;
  content: string;
  isEntryPoint?: boolean;
}

export interface ProjectFileMeta {
  path: string;
  isEntryPoint: boolean;
  size: number;
}

export interface CreateFileRequest {
  path: string;
  content?: string;
  isEntryPoint?: boolean;
}

export interface UpdateFileRequest {
  content: string;
}

export interface PatchFileRequest {
  newPath?: string;
  isEntryPoint?: boolean;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ============================================================================
// API Functions
// ============================================================================

const BASE_PATH = '/projects';

/**
 * Get all projects for the authenticated user
 */
export async function getProjects(options?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: number;
}): Promise<{ projects: StrategyProject[]; pagination: PaginationInfo }> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', options.page.toString());
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.sortBy) params.set('sortBy', options.sortBy);
  if (options?.sortOrder) params.set('sortOrder', options.sortOrder.toString());

  const queryString = params.toString() ? `?${params.toString()}` : '';
  return apiClient.get<{ projects: StrategyProject[]; pagination: PaginationInfo }>(
    `${BASE_PATH}${queryString}`
  );
}

/**
 * Get recent projects for quick access
 */
export async function getRecentProjects(limit = 10): Promise<{ projects: StrategyProject[] }> {
  return apiClient.get<{ projects: StrategyProject[] }>(
    `${BASE_PATH}/recent?limit=${limit}`
  );
}

/**
 * Get a single project by ID
 */
export async function getProject(projectId: string): Promise<{ project: StrategyProject }> {
  return apiClient.get<{ project: StrategyProject }>(`${BASE_PATH}/${projectId}`);
}

/**
 * Create a new blank project
 */
export async function createProject(
  data: CreateProjectRequest
): Promise<{ project: StrategyProject }> {
  return apiClient.post<{ project: StrategyProject }>(BASE_PATH, data);
}

/**
 * Clone a project from a template
 */
export async function cloneFromTemplate(
  templateId: string,
  name: string,
  ownershipConfirmed?: boolean
): Promise<{ project: StrategyProject }> {
  return apiClient.post<{ project: StrategyProject }>(
    `${BASE_PATH}/clone/${templateId}`,
    { name, ownershipConfirmed }
  );
}

/**
 * Update a project
 */
export async function updateProject(
  projectId: string,
  updates: UpdateProjectRequest
): Promise<{ project: StrategyProject }> {
  return apiClient.put<{ project: StrategyProject }>(
    `${BASE_PATH}/${projectId}`,
    updates
  );
}

/**
 * Delete a project
 */
export async function deleteProject(
  projectId: string,
  deleteExperiments = false
): Promise<{ message: string }> {
  const queryString = deleteExperiments ? '?deleteExperiments=true' : '';
  return apiClient.delete<{ message: string }>(`${BASE_PATH}/${projectId}${queryString}`);
}

// ============================================================================
// Code Versioning
// ============================================================================

/**
 * Get code versions for a project
 */
export async function getCodeVersions(
  projectId: string
): Promise<{ versions: CodeVersion[] }> {
  return apiClient.get<{ versions: CodeVersion[] }>(
    `${BASE_PATH}/${projectId}/versions`
  );
}

/**
 * Save current code as a tagged version
 */
export async function saveCodeVersion(
  projectId: string,
  data?: SaveVersionRequest
): Promise<{ version: CodeVersion }> {
  return apiClient.post<{ version: CodeVersion }>(
    `${BASE_PATH}/${projectId}/versions`,
    data || {}
  );
}

/**
 * Restore code from a specific version
 */
export async function restoreCodeVersion(
  projectId: string,
  versionId: string
): Promise<{ project: StrategyProject }> {
  return apiClient.post<{ project: StrategyProject }>(
    `${BASE_PATH}/${projectId}/versions/${versionId}/restore`
  );
}

// ============================================================================
// Experiments
// ============================================================================

/**
 * Get experiments (backtests) for a project
 * Returns summary data only (no trades) for performance
 */
export async function getProjectExperiments(
  projectId: string,
  options?: { limit?: number; skip?: number; versionId?: string }
): Promise<{ experiments: ProjectExperiment[] }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.skip) params.set('skip', options.skip.toString());
  if (options?.versionId) params.set('versionId', options.versionId);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  return apiClient.get<{ experiments: ProjectExperiment[] }>(
    `${BASE_PATH}/${projectId}/experiments${queryString}`
  );
}

/**
 * Get all runs (backtests + optimizations) for unified Results page
 * Returns summary data for all run types
 */
export async function getProjectRuns(
  projectId: string,
  options?: { limit?: number; versionId?: string; runType?: RunType }
): Promise<{ runs: ProjectRun[] }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.versionId) params.set('versionId', options.versionId);
  if (options?.runType) params.set('runType', options.runType);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  return apiClient.get<{ runs: ProjectRun[] }>(
    `${BASE_PATH}/${projectId}/runs${queryString}`
  );
}

/**
 * Get a single experiment (backtest) with full data including trades
 */
export async function getExperimentById(
  projectId: string,
  experimentId: string
): Promise<{ backtest: FullBacktest }> {
  return apiClient.get<{ backtest: FullBacktest }>(
    `${BASE_PATH}/${projectId}/experiments/${experimentId}`
  );
}

/**
 * Update experiment metadata (currently only runNotes)
 */
export async function updateExperimentNotes(
  projectId: string,
  experimentId: string,
  runNotes: string
): Promise<{ backtest: FullBacktest }> {
  return apiClient.patch<{ backtest: FullBacktest }>(
    `${BASE_PATH}/${projectId}/experiments/${experimentId}`,
    { runNotes }
  );
}

/**
 * Delete a single experiment (backtest) from a project
 */
export async function deleteExperiment(
  projectId: string,
  experimentId: string
): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(
    `${BASE_PATH}/${projectId}/experiments/${experimentId}`
  );
}

/**
 * Save a backtest to a project
 */
export async function saveExperimentToProject(
  projectId: string,
  data: SaveExperimentRequest
): Promise<{ backtest: ProjectExperiment }> {
  return apiClient.post<{ backtest: ProjectExperiment }>(
    `${BASE_PATH}/${projectId}/experiments`,
    data
  );
}

/**
 * Get quick-tweak children of a backtest
 */
export async function getBacktestTweaks(
  projectId: string,
  backtestId: string
): Promise<{ tweaks: ProjectRun[] }> {
  return apiClient.get<{ tweaks: ProjectRun[] }>(
    `${BASE_PATH}/${projectId}/experiments/${backtestId}/tweaks`
  );
}

// ============================================================================
// Templates
// ============================================================================

/**
 * Get available strategy templates
 */
export async function getTemplates(): Promise<{ templates: StrategyTemplate[] }> {
  return apiClient.get<{ templates: StrategyTemplate[] }>(`${BASE_PATH}/templates`);
}

/**
 * Get a single template with full code
 */
export async function getTemplate(templateId: string): Promise<{ template: StrategyTemplate }> {
  return apiClient.get<{ template: StrategyTemplate }>(
    `${BASE_PATH}/templates/${templateId}`
  );
}

// ============================================================================
// Multi-File Project Support
// ============================================================================

/**
 * Get all files in a project (metadata only, no content)
 */
export async function getProjectFiles(
  projectId: string
): Promise<{ files: ProjectFileMeta[] }> {
  return apiClient.get<{ files: ProjectFileMeta[] }>(
    `${BASE_PATH}/${projectId}/files`
  );
}

/**
 * Get a single file with content
 * Note: filePath should be URL-encoded for nested paths
 */
export async function getProjectFile(
  projectId: string,
  filePath: string
): Promise<{ file: ProjectFile }> {
  const encodedPath = encodeURIComponent(filePath);
  return apiClient.get<{ file: ProjectFile }>(
    `${BASE_PATH}/${projectId}/files/${encodedPath}`
  );
}

/**
 * Create a new file in a project
 */
export async function createProjectFile(
  projectId: string,
  data: CreateFileRequest
): Promise<{ file: ProjectFile }> {
  return apiClient.post<{ file: ProjectFile }>(
    `${BASE_PATH}/${projectId}/files`,
    data
  );
}

/**
 * Update file content
 */
export async function updateProjectFile(
  projectId: string,
  filePath: string,
  data: UpdateFileRequest
): Promise<{ file: ProjectFile }> {
  const encodedPath = encodeURIComponent(filePath);
  return apiClient.put<{ file: ProjectFile }>(
    `${BASE_PATH}/${projectId}/files/${encodedPath}`,
    data
  );
}

/**
 * Rename/move file or change entry point
 */
export async function patchProjectFile(
  projectId: string,
  filePath: string,
  data: PatchFileRequest
): Promise<{ file: ProjectFile }> {
  const encodedPath = encodeURIComponent(filePath);
  return apiClient.patch<{ file: ProjectFile }>(
    `${BASE_PATH}/${projectId}/files/${encodedPath}`,
    data
  );
}

/**
 * Delete a file from a project
 */
export async function deleteProjectFile(
  projectId: string,
  filePath: string
): Promise<{ message: string }> {
  const encodedPath = encodeURIComponent(filePath);
  return apiClient.delete<{ message: string }>(
    `${BASE_PATH}/${projectId}/files/${encodedPath}`
  );
}

// ============================================================================
// Project Intelligence
// ============================================================================

/**
 * Get full project intelligence (all dimensions + summary)
 */
export async function getProjectIntelligence(
  projectId: string
): Promise<ProjectIntelligence> {
  return apiClient.get<ProjectIntelligence>(
    `${BASE_PATH}/${projectId}/intelligence`
  );
}

/**
 * Get project intelligence summary only (lighter payload)
 */
export async function getProjectIntelligenceSummary(
  projectId: string
): Promise<ProjectIntelligenceSummary> {
  return apiClient.get<ProjectIntelligenceSummary>(
    `${BASE_PATH}/${projectId}/intelligence/summary`
  );
}

/**
 * Get project dimensions only
 */
export async function getProjectIntelligenceDimensions(
  projectId: string
): Promise<ProjectIntelligenceDimensions> {
  return apiClient.get<ProjectIntelligenceDimensions>(
    `${BASE_PATH}/${projectId}/intelligence/dimensions`
  );
}

// ============================================================================
// Default Export
// ============================================================================

export const projectsApi = {
  // Projects
  getProjects,
  getRecentProjects,
  getProject,
  createProject,
  cloneFromTemplate,
  updateProject,
  deleteProject,

  // Code Versioning
  getCodeVersions,
  saveCodeVersion,
  restoreCodeVersion,

  // Experiments & Runs
  getProjectExperiments,
  getProjectRuns,
  getExperimentById,
  updateExperimentNotes,
  deleteExperiment,
  saveExperimentToProject,
  getBacktestTweaks,

  // Templates
  getTemplates,
  getTemplate,

  // Multi-File Support
  getProjectFiles,
  getProjectFile,
  createProjectFile,
  updateProjectFile,
  patchProjectFile,
  deleteProjectFile,

  // Project Intelligence
  getProjectIntelligence,
  getProjectIntelligenceSummary,
  getProjectIntelligenceDimensions,
};

export default projectsApi;
